import { useCallback, useEffect, useState } from 'react';
import type { EffectiveRole } from '@domain/entities';
import { getAssignRoleUseCase, getGetUserRolesUseCase, getRevokeRoleUseCase } from '@di';
import { describeError } from '@shared/utils';

export function useUserRoles(userId: string | null) {
  const [roles, setRoles] = useState<EffectiveRole[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId) {
      setRoles([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      setRoles(await getGetUserRolesUseCase().execute(userId));
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const assign = useCallback(
    async (roleId: string, expiresAt?: string) => {
      if (!userId) return;

      setError(null);
      try {
        setRoles(await getAssignRoleUseCase().execute(userId, { roleId, expiresAt }));
      } catch (cause) {
        setError(describeError(cause));
      }
    },
    [userId]
  );

  const revoke = useCallback(
    async (roleId: string) => {
      if (!userId) return;

      setError(null);
      try {
        setRoles(await getRevokeRoleUseCase().execute(userId, roleId));
      } catch (cause) {
        setError(describeError(cause));
      }
    },
    [userId]
  );

  return { roles, isLoading, error, reload: load, assign, revoke };
}
